/* ============================================================
   Admin.jsx — the admin shell and the Översikt dashboard.
   ============================================================ */

import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Brand, Stat, BarChart, Thumb } from '../components/ui.jsx';
import { PageHead, PeriodToggle } from '../components/admin-shell.jsx';
import { useStore, actions } from '../store/store.js';
import {
  kr, timeAgo, rangeFor, revenueBetween, productTallies, restockList, stockValue,
  weeklySeries, monthlySeries, yearlySeries,
} from '../store/selectors.js';
import { Inventory, Schema, SalesLog, Settings, GuideEditor } from './AdminPages.jsx';
import { Inventering } from './Inventering.jsx';
import { WasteAdmin } from './Waste.jsx';
import { APP_VERSION } from '../version.js';

export function AdminApp({ onExit, initialView = 'oversikt' }) {
  const [view, setView] = useState(initialView);
  /* Menylådan på mobilen — på iPad och dator står menyn alltid framme. */
  const [menuOpen, setMenuOpen] = useState(false);
  const go = id => { setView(id); setMenuOpen(false); };
  const nav = [
    { id: 'oversikt', label: 'Översikt', icon: 'chart' },
    { id: 'varor', label: 'Varor & lager', icon: 'box' },
    { id: 'inventering', label: 'Inventering', icon: 'tag' },
    { id: 'svinn', label: 'Svinn', icon: 'trash' },
    { id: 'schema', label: 'Kioskschema', icon: 'calendar' },
    { id: 'guide', label: 'Kioskguiden', icon: 'book' },
    { id: 'forsaljning', label: 'Försäljning', icon: 'receipt' },
    { id: 'installningar', label: 'Inställningar', icon: 'gear' },
  ];

  return (
    <div className="stage admin-shell">
      <div className="admin-topbar">
        <button className="btn btn-icon" style={{ background: 'rgba(255,255,255,.1)', color: '#fff' }}
          onClick={() => setMenuOpen(true)} aria-label="Meny"><Icon name="menu" size={22} /></button>
        <Brand kind="admin" size={34} light />
      </div>
      {menuOpen && <div className="admin-scrim" onClick={() => setMenuOpen(false)} />}

      <div className={'admin-side' + (menuOpen ? ' open' : '')}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '4px 8px 20px' }}>
          <div style={{ flex: 1 }}><Brand kind="admin" size={42} light /></div>
          <button className="btn btn-icon admin-side-close" style={{ background: 'rgba(255,255,255,.1)', color: '#fff' }}
            onClick={() => setMenuOpen(false)} aria-label="Stäng menyn"><Icon name="x" size={20} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
          {nav.map(n => (
            <button key={n.id} onClick={() => go(n.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '13px 15px', borderRadius: 14, border: 'none',
                cursor: 'pointer', textAlign: 'left', fontWeight: 700, fontSize: 15, transition: 'all .12s',
                background: view === n.id ? 'var(--green)' : 'transparent',
                color: view === n.id ? '#fff' : 'rgba(255,255,255,.66)',
              }}>
              <Icon name={n.icon} size={20} />
              <span style={{ flex: 1, minWidth: 0 }}>{n.label}</span>
            </button>
          ))}
        </div>
        <button className="btn btn-block" style={{ background: 'rgba(255,255,255,.1)', color: '#fff' }} onClick={onExit}>
          <Icon name="back" size={18} /> Logga ut
        </button>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,.45)' }}>Version {APP_VERSION}</div>
      </div>

      <div className="admin-main">
        {view === 'oversikt' && <Overview />}
        {view === 'varor' && <Inventory />}
        {view === 'inventering' && <Inventering />}
        {view === 'svinn' && <WasteAdmin />}
        {view === 'schema' && <Schema />}
        {view === 'guide' && <GuideEditor />}
        {view === 'forsaljning' && <SalesLog />}
        {view === 'installningar' && <Settings />}
      </div>
    </div>
  );
}

/* ---------------- OVERVIEW ---------------- */
function Overview() {
  const state = useStore();
  const [period, setPeriod] = useState('week');
  const [selIdx, setSelIdx] = useState(null);

  function changePeriod(p) { setPeriod(p); setSelIdx(null); }

  const [from, to] = rangeFor(period);
  const cur = revenueBetween(state, from, to);
  const margin = cur.rev > 0 ? Math.round(cur.profit / cur.rev * 100) : 0;
  const avg = cur.count > 0 ? cur.rev / cur.count : 0;
  const pLabel = { week: 'denna vecka', month: 'denna månad', year: 'i år' }[period];

  /* The trend bars follow the selected period. */
  let chart, chartTitle, prefix = 'v.', labelKey = 'week';
  if (period === 'month') { chart = monthlySeries(state, 12); chartTitle = 'Intäkter per månad'; prefix = ''; labelKey = 'm'; }
  else if (period === 'year') { chart = yearlySeries(state, 3); chartTitle = 'Intäkter per år'; prefix = ''; labelKey = 'y'; }
  else { chart = weeklySeries(state, 10); chartTitle = 'Intäkter per vecka'; }

  /* Clicking a bar narrows "Säljer bäst" to that slice. */
  const selBar = selIdx != null ? chart[selIdx] : null;
  const tallies = productTallies(state, selBar ? selBar.from : from, selBar ? selBar.to : to).slice(0, 6);
  const sellLabel = selBar ? (period === 'week' ? 'v.' + selBar.week : period === 'month' ? selBar.m : selBar.y) : pLabel;

  /* Varor och lagervaror i samma lista — servetter som tar slut ska synas
     lika tydligt som korven. */
  const restock = restockList(state);
  const out = restock.filter(r => r.stock <= 0);
  const low = restock.filter(r => r.stock > 0);
  const openNotes = state.notes.filter(n => !n.done);

  return (
    <div className="scroll" style={{ height: '100%' }}>
      <PageHead title="Översikt" sub="Hur går kiosken? Intäkter, vinst och lager på ett ställe."
        right={<PeriodToggle value={period} onChange={changePeriod} />} />
      <div className="admin-body">
        <div className="grid-kpi" style={{ marginBottom: 16 }}>
          <Stat label={`Intäkter ${pLabel}`} value={kr(cur.rev)} icon="money" accent="var(--green)" sub={`${cur.count} köp`} />
          <Stat label={`Vinst ${pLabel}`} value={kr(cur.profit)} icon="chart" accent="var(--orange)" tone="var(--orange-deep)" sub={`${margin}% marginal`} />
          <Stat label="Snittköp" value={kr(avg)} icon="receipt" accent="#3f8197" />
          <Stat label="Lagervärde" value={kr(stockValue(state))} icon="box" accent="#a65a78" sub="till inköpspris" />
          <Stat label="Lågt / slut" value={`${low.length} / ${out.length}`} icon="warn" accent="var(--bad)"
            tone={low.length || out.length ? 'var(--bad)' : 'var(--ink)'} sub="varor att fylla på" />
        </div>

        <div className="grid-main">
          <div className="card" style={{ padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
              <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18 }}>{chartTitle}</span>
            </div>
            <BarChart data={chart} valueKey="rev" labelKey={labelKey} prefix={prefix} height={180}
              onBarClick={i => setSelIdx(i === selIdx ? null : i)} selected={selIdx} />
          </div>
          <div className="card" style={{ padding: '20px 22px' }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, marginBottom: 14 }}>Säljer bäst {sellLabel}</div>
            {tallies.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 14, fontWeight: 600 }}>Inga köp i perioden ännu.</div>}
            {tallies.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < tallies.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                <div className="center" style={{ width: 34, height: 34, borderRadius: 10, background: '#fbf6ec', fontSize: 18 }}>{t.emoji}</div>
                <div style={{ flex: 1, fontWeight: 700, fontSize: 14.5 }}>{t.name}</div>
                <div style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16 }}>{t.qty} st</div>
                <div style={{ width: 70, textAlign: 'right', color: 'var(--muted)', fontSize: 13.5, fontWeight: 700 }}>{kr(t.rev)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid-2" style={{ marginTop: 16 }}>
          <div className="card" style={{ padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
              <Icon name="warn" size={20} color="var(--orange)" />
              <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18 }}>Behöver påfyllning</span>
            </div>
            {restock.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 14, fontWeight: 600 }}>Allt välfyllt. 👍</div>}
            {restock.slice(0, 7).map(r => (
              <div key={r.kind + r.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 0', borderBottom: '1px solid var(--line-soft)' }}>
                <Thumb p={r} size={34} radius={10} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                  {r.kind === 'ing' && <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--orange-deep)', letterSpacing: '.4px' }}>LAGERVARA</div>}
                </div>
                {r.stock <= 0 ? <span className="pill tag-bad">SLUT</span> : <span className="pill tag-warn">{r.stock} kvar</span>}
                <button className="btn btn-sm btn-soft" onClick={() =>
                  (r.kind === 'ing' ? actions.restockIngredient : actions.restock)(r.id, r.restockQty || 24)}>
                  <Icon name="plus" size={15} /> {r.restockQty || 24}
                </button>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
              <Icon name="note" size={20} color="var(--orange)" />
              <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18 }}>Noteringar från kiosken</span>
            </div>
            {openNotes.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 14, fontWeight: 600 }}>Inga öppna noteringar.</div>}
            {openNotes.slice(0, 5).map(n => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '9px 0', borderBottom: '1px solid var(--line-soft)' }}>
                <div style={{ width: 8, height: 8, borderRadius: 9, background: 'var(--orange)', marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 14.5, lineHeight: 1.4, fontWeight: 600 }}>
                  {n.text}
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3, fontWeight: 600 }}>{timeAgo(n.ts)}</div>
                </div>
                <button className="btn btn-sm btn-soft" onClick={() => actions.toggleNote(n.id)}><Icon name="check" size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
